import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface EnrollStudentProps {
  domain: string;
}

interface Application {
  id: string;
  applicationId: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  appliedClass: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  address: string;
  city?: string;
  state?: string;
  pincode?: string;
  previousSchool?: string;
  previousClass?: string;
  nationality?: string;
  religion?: string;
  caste?: string;
  bloodGroup?: string;
  medicalConditions?: string;
  status: string;
  appliedOn: string;
}

interface EnrollmentRecord {
  applicationId: string;
  studentName: string;
  appliedClass: string;
  assignedSection: string;
  assignedRollNumber: string;
  aadhaarNumber: string;
  house: string;
  transportAvailed: string;
  parentEmail: string;
  parentPhone: string;
  parentName: string;
  status: 'pending' | 'success' | 'failed';
  error?: string;
}

const EnrollStudent: React.FC<EnrollStudentProps> = ({ domain }) => {
  const [acceptedApps, setAcceptedApps] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [enrollmentRecords, setEnrollmentRecords] = useState<EnrollmentRecord[]>([]);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'select' | 'results'>('select');

  const sections = ['A', 'B', 'C', 'D', 'E', 'F'];
  const houses = ['Red', 'Blue', 'Green', 'Yellow'];

  useEffect(() => {
    loadAcceptedApplications();
  }, [domain]);

  const loadAcceptedApplications = async () => {
    setLoading(true);
    try {
      const applicationsRef = collection(db, `school-data/${domain}/applications`);
      const q = query(applicationsRef, where('status', '==', 'accepted'));
      const snapshot = await getDocs(q);

      const appsList: Application[] = [];
      snapshot.forEach((docSnap) => {
        appsList.push({
          id: docSnap.id,
          ...docSnap.data(),
        } as Application);
      });

      setAcceptedApps(appsList);

      // Initialize enrollment records
      const records: EnrollmentRecord[] = appsList.map(app => ({
        applicationId: app.applicationId,
        studentName: app.studentName,
        appliedClass: app.appliedClass,
        assignedSection: 'A', // Default section
        assignedRollNumber: '',
        aadhaarNumber: '',
        house: 'Red', // Default house
        transportAvailed: 'N',
        parentEmail: app.email || '', // Use application email as parent email
        parentPhone: app.phone || '',
        parentName: app.fatherName || '', // Auto-fill from application
        status: 'pending',
      }));
      setEnrollmentRecords(records);
    } catch (error: any) {
      console.error('Error loading applications:', error);
      alert(`Failed to load applications: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateEnrollmentRecord = (applicationId: string, field: keyof EnrollmentRecord, value: string) => {
    setEnrollmentRecords(prev =>
      prev.map(record =>
        record.applicationId === applicationId
          ? { ...record, [field]: value }
          : record
      )
    );
  };

  const generateRollNumber = () => {
    // Generate a 5-digit roll number (format: 00001, 00002, etc.)
    const timestamp = Date.now().toString().slice(-5);
    return timestamp.padStart(5, '0');
  };

  const generateEnrollmentId = (rollNumber: string) => {
    const year = new Date().getFullYear().toString().slice(-2);
    return `${year}${domain}${rollNumber}`;
  };

  const autoAssignRollNumbers = () => {
    const updated = enrollmentRecords.map(record => ({
      ...record,
      assignedRollNumber: record.assignedRollNumber || generateRollNumber(),
    }));
    setEnrollmentRecords(updated);
  };

  const executeEnrollment = async () => {
    // Validate all records have roll numbers
    const missingRollNumbers = enrollmentRecords.filter(r => !r.assignedRollNumber);
    if (missingRollNumbers.length > 0) {
      alert('Please assign roll numbers to all students before enrolling.');
      return;
    }

    if (!confirm(`Are you sure you want to enroll ${enrollmentRecords.length} students? This will create student profiles.`)) {
      return;
    }

    setProcessing(true);
    const updatedRecords: EnrollmentRecord[] = [];

    for (let i = 0; i < enrollmentRecords.length; i++) {
      const record = enrollmentRecords[i];
      const application = acceptedApps.find(app => app.applicationId === record.applicationId);

      if (!application) {
        updatedRecords.push({
          ...record,
          status: 'failed',
          error: 'Application not found',
        });
        continue;
      }

      try {
        // Generate enrollment ID
        const enrollmentId = generateEnrollmentId(record.assignedRollNumber);

        // Generate student document ID: {enrollmentId}_student_{class}_{section}
        const studentDocId = `${enrollmentId}_student_${record.appliedClass}_${record.assignedSection}`;

        // Generate class name (e.g., "Class 10A")
        const className = `Class ${record.appliedClass}${record.assignedSection}`;

        // Create student profile
        const studentData = {
          // Basic Information
          studentId: studentDocId,
          enrollmentId: enrollmentId,
          name: application.studentName,
          class: record.appliedClass,
          className: className,
          section: record.assignedSection,
          rollNumber: record.assignedRollNumber,

          // Personal Details
          dateOfBirth: application.dateOfBirth,
          gender: application.gender,
          bloodGroup: application.bloodGroup || '',
          nationality: application.nationality || 'Indian',
          religion: application.religion || '',
          caste: application.caste || '',
          aadhaarNumber: record.aadhaarNumber || '',

          // Parent Information
          fatherName: application.fatherName,
          motherName: application.motherName || '',
          parentName: record.parentName,
          parentEmail: record.parentEmail,
          parentPhone: record.parentPhone,

          // Contact Information
          email: application.email,
          phone: application.phone,
          alternatePhone: application.alternatePhone || '',
          address: application.address,
          city: application.city || '',
          state: application.state || '',
          pincode: application.pincode || '',

          // Previous Education
          previousSchool: application.previousSchool || '',
          previousClass: application.previousClass || '',

          // Medical Information
          medicalConditions: application.medicalConditions || '',

          // School-specific Information
          house: record.house,
          transportAvailed: record.transportAvailed,

          // Admission Details
          applicationId: application.applicationId,
          admissionDate: new Date().toISOString(),
          academicYear: new Date().getFullYear().toString(),
          status: 'active',

          // Timestamps
          createdOn: new Date().toISOString(),
          updatedOn: new Date().toISOString(),
        };

        // Create student document
        const studentRef = doc(db, `school-users/${domain}/students`, studentDocId);
        await setDoc(studentRef, studentData);

        // Update application status to 'enrolled'
        const appRef = doc(db, `school-data/${domain}/applications`, application.id);
        await updateDoc(appRef, {
          status: 'enrolled',
          enrolledOn: new Date().toISOString(),
          studentDocId: studentDocId,
          enrollmentId: enrollmentId,
        });

        updatedRecords.push({
          ...record,
          status: 'success',
        });
      } catch (error: any) {
        console.error(`Error enrolling student ${record.studentName}:`, error);
        updatedRecords.push({
          ...record,
          status: 'failed',
          error: error.message,
        });
      }
    }

    setEnrollmentRecords(updatedRecords);
    setProcessing(false);
    setActiveTab('results');
  };

  const downloadReport = () => {
    const reportData = enrollmentRecords.map(record => ({
      'Application ID': record.applicationId,
      'Student Name': record.studentName,
      'Class': record.appliedClass,
      'Section': record.assignedSection,
      'Roll Number': record.assignedRollNumber,
      'Status': record.status.toUpperCase(),
      'Error': record.error || '',
    }));

    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Enrollment Report');

    XLSX.writeFile(wb, `student_enrollment_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const successCount = enrollmentRecords.filter(r => r.status === 'success').length;
  const failedCount = enrollmentRecords.filter(r => r.status === 'failed').length;

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-person-plus-fill me-2 text-primary-custom"></i>
          Enroll Students
        </h5>
        <small className="text-muted">Convert accepted applications into student profiles</small>
      </div>

      <div className="card-body">
        {/* Tab Navigation */}
        <ul className="nav nav-pills mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'select' ? 'active' : ''}`}
              onClick={() => setActiveTab('select')}
              disabled={processing}
            >
              <i className="bi bi-1-circle me-2"></i>
              Assign Details
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'results' ? 'active' : ''}`}
              disabled={successCount === 0 && failedCount === 0}
            >
              <i className="bi bi-2-circle me-2"></i>
              Results
            </button>
          </li>
        </ul>

        {/* Step 1: Assign Details */}
        {activeTab === 'select' && (
          <div>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading accepted applications...</p>
              </div>
            ) : acceptedApps.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox display-1 text-muted"></i>
                <p className="mt-3 text-muted">No accepted applications found for enrollment</p>
              </div>
            ) : (
              <div>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>{acceptedApps.length} accepted applications</strong> ready for enrollment. Assign sections and roll numbers before proceeding.
                </div>

                <div className="mb-3">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={autoAssignRollNumbers}
                  >
                    <i className="bi bi-magic me-2"></i>
                    Auto-Assign Roll Numbers
                  </button>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Application ID</th>
                        <th>Student Name</th>
                        <th>Father's Name</th>
                        <th>Class</th>
                        <th>Section</th>
                        <th>Roll Number</th>
                        <th>Aadhaar Number</th>
                        <th>House</th>
                        <th>Transport</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrollmentRecords.map((record) => {
                        const app = acceptedApps.find(a => a.applicationId === record.applicationId);
                        return (
                          <tr key={record.applicationId}>
                            <td><strong>{record.applicationId}</strong></td>
                            <td>{record.studentName}</td>
                            <td>{app?.fatherName}</td>
                            <td>
                              <span className="badge bg-primary">Class {record.appliedClass}</span>
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ width: '100px' }}
                                value={record.assignedSection}
                                onChange={(e) =>
                                  updateEnrollmentRecord(record.applicationId, 'assignedSection', e.target.value)
                                }
                              >
                                {sections.map(sec => (
                                  <option key={sec} value={sec}>Section {sec}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ width: '100px' }}
                                placeholder="00001"
                                value={record.assignedRollNumber}
                                onChange={(e) =>
                                  updateEnrollmentRecord(record.applicationId, 'assignedRollNumber', e.target.value)
                                }
                                maxLength={5}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ width: '140px' }}
                                placeholder="XXXX-XXXX-XXXX"
                                value={record.aadhaarNumber}
                                onChange={(e) =>
                                  updateEnrollmentRecord(record.applicationId, 'aadhaarNumber', e.target.value)
                                }
                                maxLength={12}
                              />
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ width: '100px' }}
                                value={record.house}
                                onChange={(e) =>
                                  updateEnrollmentRecord(record.applicationId, 'house', e.target.value)
                                }
                              >
                                {houses.map(h => (
                                  <option key={h} value={h}>{h}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                style={{ width: '80px' }}
                                value={record.transportAvailed}
                                onChange={(e) =>
                                  updateEnrollmentRecord(record.applicationId, 'transportAvailed', e.target.value)
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex gap-2 mt-4">
                  <button
                    className="btn btn-success btn-lg"
                    onClick={executeEnrollment}
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Enrolling Students...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Enroll {enrollmentRecords.length} Students
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-secondary btn-lg"
                    onClick={() => loadAcceptedApplications()}
                    disabled={processing}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Results */}
        {activeTab === 'results' && (
          <div>
            <div className="alert alert-success">
              <h5 className="alert-heading">
                <i className="bi bi-check-circle me-2"></i>
                Enrollment Complete
              </h5>
              <p className="mb-0">
                <strong>{successCount}</strong> students enrolled successfully.
                {failedCount > 0 && (
                  <span className="text-danger ms-2">
                    <strong>{failedCount}</strong> failed.
                  </span>
                )}
              </p>
            </div>

            <div className="mb-3">
              <button className="btn btn-primary" onClick={downloadReport}>
                <i className="bi bi-file-earmark-excel me-2"></i>
                Download Report
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Student Name</th>
                    <th>Application ID</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Roll Number</th>
                    <th>Student ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollmentRecords.map((record) => (
                    <tr key={record.applicationId}>
                      <td>{record.studentName}</td>
                      <td>{record.applicationId}</td>
                      <td>Class {record.appliedClass}</td>
                      <td>Section {record.assignedSection}</td>
                      <td>{record.assignedRollNumber}</td>
                      <td>
                        {record.status === 'success' && (
                          <code>student_{record.appliedClass}_{record.assignedSection}_{record.assignedRollNumber}</code>
                        )}
                      </td>
                      <td>
                        {record.status === 'success' ? (
                          <span className="badge bg-success">Success</span>
                        ) : record.status === 'failed' ? (
                          <span className="badge bg-danger" title={record.error}>
                            Failed: {record.error}
                          </span>
                        ) : (
                          <span className="badge bg-secondary">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setActiveTab('select');
                  loadAcceptedApplications();
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Enrollment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnrollStudent;
